import { FeatureToggleKey } from "@omni/types/enums/featureToggleKey.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { useWishlistService } from "@/api/hooks/wishlistService.hook";
import {
    HeaderButton,
    HeaderButtonProps
} from "@/components/General/HeaderButton/headerButton.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { useFeatureToggle } from "@/context/featureToggles.context";
import { useThunkDispatch } from "@/redux/dispatcher";
import { getCustomer } from "@/redux/modules/api/data/customer/customer.selectors";
import * as WishlistModule from "@/redux/modules/api/data/wishlist/wishlist.module";
import * as WishlistSelectors from "@/redux/modules/api/data/wishlist/wishlist.selectors";
import { WISHLIST_MESSAGE_ID } from "@/redux/modules/api/data/wishlist/wishlist.utils";
import * as HeaderSelectors from "@/redux/modules/value/header/header.selectors";
import CallService, { CallServiceKey } from "@/services/call.service";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { isPageTemplate } from "@/utils/pageTemplate.utils";

import "./wishlistHeaderButton.scss";

export type WishlistHeaderButtonContainerProps = HeaderButtonProps;

const WishlistHeaderButtonContainer = (
    props: WishlistHeaderButtonContainerProps
) => {
    const { invert, text, link } = props;

    const isClientSide = useIsClientSide();
    const wishlists = useSelector(WishlistSelectors.getWishlists);
    const mobile = useSelector(HeaderSelectors.isMobile);
    const dispatch = useThunkDispatch();
    const customer = useSelector(getCustomer);
    const isLoggedIn = customer && !customer.guest;

    const wishlistBadgeEnabled = useFeatureToggle(
        FeatureToggleKey.FEATURE_WISHLIST_BADGE
    );

    const [{ data }, loadWishlist] = useWishlistService(
        {
            url: "/products/count",
            method: "GET"
        },
        {
            manual: true
        }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isClientSide) {
            if (wishlistBadgeEnabled) {
                loadWishlist().then(
                    (payload) => {
                        if (payload.data > 0) {
                            CallService.enable(CallServiceKey.Wishlist);
                        } else {
                            CallService.disable(CallServiceKey.Wishlist);
                        }
                    },
                    () => {
                        CallService.enable(CallServiceKey.Wishlist);
                    }
                );
            } else {
                dispatch(WishlistModule.loadAll());
            }
        }
    }, [isClientSide]);

    const componentShouldInvert = isClientSide ? invert || mobile : false;

    const productCount = wishlists.reduce(
        (acc, wishlist) => acc + wishlist?.products?.length || 0,
        0
    );
    const itemCount = isClientSide ? (productCount ? productCount : data) : 0;

    const showMessagesContainer = !(
        isPageTemplate(PageTemplate.ShoppingCart) && isLoggedIn
    );

    return (
        <div className="mx-1">
            <HeaderButton
                {...props}
                invert={componentShouldInvert}
                text={text}
                hover={true}
                addCloseCallback={true}
                iconPrefix="lar"
                link={link}
                alerts={(itemCount || 0) < 100 ? itemCount : "+99"}
            ></HeaderButton>
            {isClientSide &&
                showMessagesContainer &&
                createPortal(
                    <Messages
                        messageid={WISHLIST_MESSAGE_ID}
                        className={
                            "wishlistHeaderButton__messages position-fixed mx-3"
                        }
                    />,
                    document.querySelector("body")!
                )}
        </div>
    );
};

export { WishlistHeaderButtonContainer };
